<template>
  <span>
    <!-- CREATE BANK ACCOUNT MODAL -->
    <BankAccountDialog
      v-model="openCreateAccDialog"
      :companyID="id"
      :companyName="companyName"
      :accounts="bankAccounts"
      :createEUR="hasGBPAcc"
      :createGBP="hasEURAcc"
      @dialog-closed="closeDialog"
    />
    <v-card class="success-card">
      <v-card-title class="justify-space-between">
        <div class="company-box-title pb-0">Accounts</div>
        <div :class="{ 'pointer-not-allowed': !hasPermission.addBankAccounts }">
          <v-tooltip
            v-if="!canCreateBankAccounts || !hasPermission.addBankAccounts"
            :disabled="!hasPermission.addBankAccounts"
            bottom
          >
            <template #activator="{ on }">
              <v-btn
                text
                icon
                small
                :disabled="!canCreateBankAccounts"
                :class="{ 'disable-events': !hasPermission.addBankAccounts }"
                @click="openCreateDialog"
                v-on="on"
              >
                <v-icon>add</v-icon>
              </v-btn>
            </template>
            <span>{{
              !hasPermission.addBankAccounts
                ? notAccessMessage
                : maxAccPermittedMsg
            }}</span>
          </v-tooltip>
          <v-btn
            v-else
            text
            icon
            small
            @click="openCreateDialog"
          >
            <v-icon>add</v-icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-card-text class="pt-0">
        <bank-accounts-table
          v-if="hasEURAcc"
          isEUR
          :id="id"
          :accounts="filteredAccountsByCurrency('EUR')"
          :headers="headersEUR"
          :loading="loading"
          @dialog-closed="closeDialog"
        />
        <bank-accounts-table
          v-if="hasGBPAcc"
          :id="id"
          :accounts="filteredAccountsByCurrency('GBP')"
          :headers="headersGBP"
          :loading="loading"
          @dialog-closed="closeDialog"
        />
      </v-card-text>
    </v-card>
  </span>
</template>

<script>
import * as R from 'ramda'
import { NOT_ACCESS_MESSAGE } from 'src/constants.js'
import BankAccountDialog from '../../../../../components/accounts-dialog/bank-account-dialog.vue'
import { getCompanyBankAccounts } from '../../../apiCalls'
import BankAccountsTable from './bank-accounts-table.vue'
import { EUR_HEADERS, GBP_HEADERS } from './helpers'

const isDefined = R.complement(R.converge(R.or, [R.isNil, R.isEmpty]))

export default {
  name: 'BankAccounts',
  components: {
    BankAccountsTable,
    BankAccountDialog
  },
  data() {
    return {
      headersEUR: EUR_HEADERS,
      headersGBP: GBP_HEADERS,
      openCreateAccDialog: false,
      bankAccounts: [],
      loading: false,
      notAccessMessage: NOT_ACCESS_MESSAGE
    }
  },
  props: {
    id: { type: String, required: true },
    companyName: { type: String, required: true }
  },
  computed: {
    maxAccPermittedMsg() {
      return 'Cannot have more than one EUR or GBP account'
    },
    hasPermission() {
      return this.$store.getters.hasPermission
    },
    hasEURAcc() {
      return isDefined(R.find(R.propEq('currency', 'EUR'), this.bankAccounts))
    },
    hasGBPAcc() {
      return isDefined(R.find(R.propEq('currency', 'GBP'), this.bankAccounts))
    },
    canCreateBankAccounts() {
      return R.isEmpty(this.bankAccounts) || !this.hasEURAcc || !this.hasGBPAcc
    }
  },
  methods: {
    openCreateDialog() {
      this.openCreateAccDialog = true
    },
    filteredAccountsByCurrency(currencyToFilterBy) {
      return R.filter(
        (bankAccount) => bankAccount.currency === currencyToFilterBy,
        this.bankAccounts
      )
    },
    async closeDialog(value) {
      this.openCreateAccDialog = false
      if (value) {
        this.$emit('company-bank-account-changed')
        await this.getBankAccounts()
      }
    },
    async getBankAccounts() {
      this.loading = true
      try {
        const res = await getCompanyBankAccounts(this.id)
        this.bankAccounts = res.company || []
      } catch (e) {
        this.$error(e)
        this.notifyError({
          message:
            'There was a problem with bank accounts. Contact with Tech team.'
        })
      }
      this.loading = false
    }
  },
  mounted() {
    this.getBankAccounts()
  }
}
</script>
